import type { WhereCategory, WhereDataset, WhereField, WhereOption } from '@parcel/filters';
import {
  Command,
  CommandBareInput,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@parcel/ui';
import { ChevronLeft, ChevronRight, Search, Sparkles } from 'lucide-react';
import * as React from 'react';
import { type RecordMatch, readRecentFields } from '../filter-bar/filter-menu.js';
import { type WhereOptions, deriveWhereMenu, menuLabel } from './where-menu.js';

export interface WhereSearchInputProps {
  dataset: WhereDataset;
  options: WhereOptions;
  /** Table id for the recently used fields store. */
  recentKey: string;
  placeholder?: string;
  onPickField: (field: WhereField) => void;
  onPickValue: (field: WhereField, value: string) => void;
  /** "Search for …": the free-text Search chip. */
  onSearchText?: (text: string) => void;
  /** Enables the Ask rows; the caller owns the in-flight state and the merge. */
  onAsk?: (query: string) => void;
  askExample?: string;
  records?: readonly RecordMatch[];
  onSelectRecord?: (id: string) => void;
  onInputChange?: (text: string) => void;
  /** Backspace in an empty input: the bar opens its last chip. */
  onBackspaceEmpty?: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
}

/** The pill input and its menu: two levels (category, then "Project | City"), value matches, Ask and search rows. */
export function WhereSearchInput({
  dataset,
  options,
  recentKey,
  placeholder = 'Search, filter or ask…',
  onPickField,
  onPickValue,
  onSearchText,
  onAsk,
  askExample,
  records = [],
  onSelectRecord,
  onInputChange,
  onBackspaceEmpty,
  inputRef: externalInputRef,
}: WhereSearchInputProps) {
  const [input, setInput] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [category, setCategory] = React.useState<WhereCategory | null>(null);
  const commandRef = React.useRef<HTMLDivElement>(null);
  const localInputRef = React.useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef ?? localInputRef;

  // Suggested refreshes only on open, so rows never jump mid-interaction.
  const recentKeys = React.useMemo(
    () => (open ? readRecentFields(window.localStorage, recentKey) : []),
    [open, recentKey],
  );
  const menu = React.useMemo(
    () => deriveWhereMenu(input, dataset, { options, recentKeys }),
    [input, dataset, options, recentKeys],
  );
  const openCategory = category ? menu.categories.find((c) => c.category === category) : undefined;

  const setText = (text: string) => {
    setInput(text);
    onInputChange?.(text);
    if (text) setCategory(null);
  };

  const close = React.useCallback(() => {
    setOpen(false);
    setCategory(null);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!commandRef.current?.contains(event.target as Node)) close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, close]);

  const pickField = (field: WhereField) => {
    setText('');
    close();
    onPickField(field);
  };
  const pickValue = (field: WhereField, option: WhereOption) => {
    setText('');
    close();
    onPickValue(field, option.value);
  };

  const fieldRow = (field: WhereField) => {
    const { category: group, label } = menuLabel(dataset, field);
    return (
      <CommandItem
        key={field.key}
        value={`field:${field.key}`}
        onSelect={() => pickField(field)}
        className="px-3 py-2"
      >
        {group ? (
          <>
            <span className="text-muted-foreground">{group}</span>
            <span className="text-muted-foreground/60" aria-hidden>
              |
            </span>
          </>
        ) : null}
        <span className="truncate">{label}</span>
      </CommandItem>
    );
  };

  const hasTypingMatches =
    records.length > 0 || menu.fieldMatches.length > 0 || menu.valueMatches.length > 0;

  return (
    <Command
      ref={commandRef}
      shouldFilter={false}
      className="w-auto max-w-[300px] flex-1 overflow-visible bg-transparent"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          if (category) setCategory(null);
          else close();
          event.preventDefault();
          return;
        }
        if (event.key === 'Backspace' && event.target === inputRef.current && input.length === 0) {
          if (category) setCategory(null);
          else onBackspaceEmpty?.();
        }
      }}
    >
      <div className="flex h-9 min-w-[220px] items-center gap-2 rounded-full border bg-background px-3 ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <CommandBareInput
          ref={inputRef}
          value={input}
          onValueChange={setText}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
        />
      </div>

      {open ? (
        <div className="absolute left-0 top-10 z-50 w-[480px] max-w-[80vw] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md">
          {onAsk && askExample && menu.searchText === null && !openCategory ? (
            <div className="flex items-center gap-1.5 border-b bg-brand-muted px-3 py-2.5 text-sm">
              <span className="font-medium text-brand">Ask:</span>
              <span className="truncate text-muted-foreground">{askExample}</span>
              <Sparkles className="ml-auto h-4 w-4 shrink-0 text-brand" aria-hidden />
            </div>
          ) : null}
          <CommandList className="max-h-[min(380px,55vh)]">
            {menu.searchText === null ? (
              openCategory ? (
                <CommandGroup>
                  <CommandItem
                    value="back"
                    onSelect={() => setCategory(null)}
                    className="px-3 py-2 text-muted-foreground"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden />
                    {openCategory.label} fields
                  </CommandItem>
                  {openCategory.fields.map(fieldRow)}
                </CommandGroup>
              ) : (
                <>
                  <CommandGroup heading="Suggested">{menu.suggested.map(fieldRow)}</CommandGroup>
                  <CommandGroup heading="All filters">
                    {menu.categories.map((c) => (
                      <CommandItem
                        key={c.category}
                        value={`category:${c.category}`}
                        onSelect={() => setCategory(c.category)}
                        className="px-3 py-2"
                      >
                        <span className="truncate">{c.label}</span>
                        <span className="ml-auto text-xs text-muted-foreground">
                          {c.fields.length}
                        </span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )
            ) : (
              <>
                {records.length > 0 ? (
                  <CommandGroup heading="Records">
                    {records.map((record) => (
                      <CommandItem
                        key={record.id}
                        value={`record:${record.id}`}
                        onSelect={() => {
                          onSelectRecord?.(record.id);
                          setText('');
                          close();
                        }}
                      >
                        <span className="truncate">{record.label}</span>
                        {record.sublabel ? (
                          <span className="truncate text-xs text-muted-foreground">
                            · {record.sublabel}
                          </span>
                        ) : null}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ) : null}
                {menu.fieldMatches.length > 0 ? (
                  <CommandGroup heading="Filters">{menu.fieldMatches.map(fieldRow)}</CommandGroup>
                ) : null}
                {menu.valueMatches.length > 0 ? (
                  <CommandGroup heading="Values">
                    {menu.valueMatches.map(({ field, option }) => {
                      const { category: group, label } = menuLabel(dataset, field);
                      return (
                        <CommandItem
                          key={`${field.key}:${option.value}`}
                          value={`value:${field.key}:${option.value}`}
                          onSelect={() => pickValue(field, option)}
                        >
                          <span className="text-muted-foreground">
                            {group ? `${group} ${label.toLowerCase()}` : label}
                          </span>
                          <ChevronRight className="h-3 w-3 text-muted-foreground" aria-hidden />
                          <span className="truncate">{option.label}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ) : null}
                {hasTypingMatches ? <CommandSeparator alwaysRender /> : null}
                {/* DOM order is cmdk's Enter default: with no matches a full sentence goes to Ask. */}
                <CommandGroup>
                  {onAsk ? (
                    <CommandItem
                      value="ask-row"
                      onSelect={() => {
                        const question = menu.searchText ?? '';
                        setText('');
                        close();
                        onAsk(question);
                      }}
                      className="aria-selected:!bg-brand-muted"
                    >
                      <span className="font-medium text-brand">Ask:</span>
                      <span className="truncate">“{menu.searchText}”</span>
                      <Sparkles className="ml-auto h-4 w-4 shrink-0 text-brand" aria-hidden />
                    </CommandItem>
                  ) : null}
                  {onSearchText ? (
                    <CommandItem
                      value="search-row"
                      onSelect={() => {
                        const text = menu.searchText ?? '';
                        setText('');
                        close();
                        onSearchText(text);
                      }}
                    >
                      <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
                      Search for “{menu.searchText}”
                    </CommandItem>
                  ) : null}
                </CommandGroup>
              </>
            )}
          </CommandList>
          <div className="flex items-center justify-between border-t px-3 py-1.5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd> navigate
            </span>
            <span className="flex items-center gap-1">
              <Kbd>⏎</Kbd> select
            </span>
          </div>
        </div>
      ) : null}
    </Command>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border bg-muted px-1 font-sans text-[10px] text-muted-foreground">
      {children}
    </kbd>
  );
}
